import * as THREE from 'three';
import type { TimeOfDay } from '../story/episode';

export interface WorldDiagnostics {
  meshes: number;
  instancedMeshes: number;
  geometries: number;
  materials: number;
  textures: number;
  triangles: number;
}

export interface LakesideVillageWorld {
  root: THREE.Group;
  anchors: Record<string, THREE.Vector3>;
  collisions: THREE.Object3D[];
  diagnostics: WorldDiagnostics;
  update(deltaSeconds: number, elapsedSeconds: number): void;
  setTimeOfDay(time: TimeOfDay): void;
  dispose(): void;
}

export interface VillageLightingRig {
  root: THREE.Group;
  sun: THREE.DirectionalLight;
  hemisphere: THREE.HemisphereLight;
  rim: THREE.DirectionalLight;
  setTimeOfDay(time: TimeOfDay): void;
}

const PALETTE: Record<TimeOfDay, { skyTop: string; skyHorizon: string; fog: string; sun: string; sunIntensity: number; hemi: number; practical: number }> = {
  morning: { skyTop: '#8cb9c3', skyHorizon: '#f5d6aa', fog: '#c9d6c8', sun: '#ffe0a4', sunIntensity: 2.7, hemi: 1.35, practical: 0.45 },
  golden: { skyTop: '#7da6b2', skyHorizon: '#f4ae78', fog: '#d5b58d', sun: '#ffc477', sunIntensity: 3.1, hemi: 1.2, practical: 0.75 },
  forest: { skyTop: '#567f78', skyHorizon: '#a8b997', fog: '#6f8d7d', sun: '#d7e0a4', sunIntensity: 2.0, hemi: 1.05, practical: 0.95 },
  'blue-hour': { skyTop: '#334b74', skyHorizon: '#9a83a8', fog: '#5c6881', sun: '#dfc6d5', sunIntensity: 1.05, hemi: 0.82, practical: 2.2 },
};

const textureCache = new Set<THREE.Texture>();

function makeGroundTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 512;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create village texture.');
  context.fillStyle = '#708266';
  context.fillRect(0, 0, 512, 512);
  for (let y = 0; y < 512; y += 32) {
    for (let x = 0; x < 512; x += 32) {
      const seed = ((x * 13 + y * 7) % 47) / 47;
      context.fillStyle = seed > 0.52 ? 'rgba(227,216,167,.045)' : 'rgba(28,63,50,.05)';
      context.beginPath();
      context.arc(x + 10 + seed * 14, y + 12, 4 + seed * 6, 0, Math.PI * 2);
      context.fill();
    }
  }
  context.strokeStyle = 'rgba(49,70,52,.16)';
  context.lineWidth = 3;
  for (let i = 0; i < 32; i += 1) {
    const x = (i * 83) % 512;
    context.beginPath();
    context.moveTo(x, (i * 37) % 512);
    context.quadraticCurveTo(x + 16, ((i * 37) % 512) + 10, x + 28, ((i * 37) % 512) - 3);
    context.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(6, 6);
  texture.anisotropy = 4;
  textureCache.add(texture);
  return texture;
}

function addShadowed(parent: THREE.Object3D, mesh: THREE.Mesh): THREE.Mesh {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
}

function createCabin(materials: MaterialKit, scale = 1): THREE.Group {
  const cabin = new THREE.Group();
  const base = addShadowed(cabin, new THREE.Mesh(new THREE.BoxGeometry(3.3, 2.25, 2.8), materials.wood));
  base.position.y = 1.12;
  const roof = addShadowed(cabin, new THREE.Mesh(new THREE.ConeGeometry(2.65, 1.45, 4), materials.roof));
  roof.position.y = 2.9;
  roof.rotation.y = Math.PI / 4;
  roof.scale.z = 0.83;
  const door = addShadowed(cabin, new THREE.Mesh(new THREE.BoxGeometry(0.72, 1.45, 0.12), materials.darkWood));
  door.position.set(0, 0.78, 1.46);
  for (const x of [-0.95, 0.95]) {
    const frame = addShadowed(cabin, new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.82, 0.13), materials.trim));
    frame.position.set(x, 1.35, 1.47);
    const window = new THREE.Mesh(new THREE.PlaneGeometry(0.54, 0.62), materials.window);
    window.position.set(x, 1.35, 1.545);
    cabin.add(window);
  }
  const chimney = addShadowed(cabin, new THREE.Mesh(new THREE.BoxGeometry(0.46, 1.35, 0.46), materials.stone));
  chimney.position.set(0.82, 3.18, -0.35);
  cabin.scale.setScalar(scale);
  return cabin;
}

function createRangerStation(materials: MaterialKit): THREE.Group {
  const station = createCabin(materials, 1.32);
  station.name = 'rangerStation';
  const tower = new THREE.Group();
  const mast = addShadowed(tower, new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.18, 4.5, 8), materials.darkWood));
  mast.position.y = 2.25;
  const lookout = addShadowed(tower, new THREE.Mesh(new THREE.BoxGeometry(2.1, 1.25, 2.1), materials.wood));
  lookout.position.y = 4.3;
  const cap = addShadowed(tower, new THREE.Mesh(new THREE.ConeGeometry(1.75, 1.05, 4), materials.roof));
  cap.position.y = 5.35;
  cap.rotation.y = Math.PI / 4;
  const railGeometry = new THREE.CylinderGeometry(0.045, 0.045, 2.3, 6);
  for (let i = 0; i < 8; i += 1) {
    const angle = (i / 8) * Math.PI * 2;
    const rail = new THREE.Mesh(railGeometry, materials.trim);
    rail.position.set(Math.cos(angle) * 1.28, 3.95, Math.sin(angle) * 1.28);
    tower.add(rail);
  }
  tower.position.set(-2.1, 0, -0.6);
  station.add(tower);
  const badge = new THREE.Mesh(new THREE.CircleGeometry(0.48, 7), materials.badge);
  badge.position.set(0, 2.25, 1.88);
  station.add(badge);
  return station;
}

function createRopeBridge(materials: MaterialKit): THREE.Group {
  const bridge = new THREE.Group();
  bridge.name = 'ropeBridge';
  const plankGeometry = new THREE.BoxGeometry(0.72, 0.13, 2.15);
  const ropeCurveLeft: THREE.Vector3[] = [];
  const ropeCurveRight: THREE.Vector3[] = [];
  for (let i = 0; i < 18; i += 1) {
    const x = (i - 8.5) * 0.72;
    const sag = -Math.sin((i / 17) * Math.PI) * 0.75;
    const plank = addShadowed(bridge, new THREE.Mesh(plankGeometry, materials.bridgeWood));
    plank.position.set(x, 1.8 + sag, 0);
    plank.rotation.z = Math.sin(i * 1.7) * 0.02;
    ropeCurveLeft.push(new THREE.Vector3(x, 2.85 + sag * 0.55, -1.02));
    ropeCurveRight.push(new THREE.Vector3(x, 2.85 + sag * 0.55, 1.02));
  }
  for (const points of [ropeCurveLeft, ropeCurveRight]) {
    const rope = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 64, 0.045, 6, false), materials.rope);
    bridge.add(rope);
  }
  for (const side of [-1, 1]) {
    for (const x of [-6.5, 6.5]) {
      const post = addShadowed(bridge, new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.16, 3.2, 8), materials.darkWood));
      post.position.set(x, 2.25, side);
    }
  }
  return bridge;
}

function createLantern(materials: MaterialKit): THREE.Group {
  const lantern = new THREE.Group();
  const post = addShadowed(lantern, new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.11, 3.2, 8), materials.metal));
  post.position.y = 1.6;
  const cap = addShadowed(lantern, new THREE.Mesh(new THREE.ConeGeometry(0.45, 0.38, 6), materials.roof));
  cap.position.y = 3.28;
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.23, 12, 8), materials.lamp);
  bulb.name = 'practicalLight';
  bulb.position.y = 2.95;
  lantern.add(bulb);
  return lantern;
}

function createSky(material: THREE.ShaderMaterial): THREE.Mesh {
  const sky = new THREE.Mesh(new THREE.SphereGeometry(180, 32, 16), material);
  sky.frustumCulled = false;
  return sky;
}

interface MaterialKit {
  ground: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial;
  darkWood: THREE.MeshStandardMaterial;
  bridgeWood: THREE.MeshStandardMaterial;
  roof: THREE.MeshStandardMaterial;
  trim: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial;
  badge: THREE.MeshStandardMaterial;
  window: THREE.MeshStandardMaterial;
  pine: THREE.MeshStandardMaterial;
  pineLight: THREE.MeshStandardMaterial;
  trunk: THREE.MeshStandardMaterial;
  rope: THREE.MeshStandardMaterial;
  metal: THREE.MeshStandardMaterial;
  lamp: THREE.MeshStandardMaterial;
  water: THREE.MeshPhysicalMaterial;
}

function makeMaterials(): MaterialKit {
  return {
    ground: new THREE.MeshStandardMaterial({ color: '#718068', map: makeGroundTexture(), roughness: 0.96 }),
    wood: new THREE.MeshStandardMaterial({ color: '#a47752', roughness: 0.8 }),
    darkWood: new THREE.MeshStandardMaterial({ color: '#5c4539', roughness: 0.88 }),
    bridgeWood: new THREE.MeshStandardMaterial({ color: '#9c7653', roughness: 0.9 }),
    roof: new THREE.MeshStandardMaterial({ color: '#3f6b63', roughness: 0.78 }),
    trim: new THREE.MeshStandardMaterial({ color: '#ead8af', roughness: 0.72 }),
    stone: new THREE.MeshStandardMaterial({ color: '#8d9184', roughness: 0.94 }),
    badge: new THREE.MeshStandardMaterial({ color: '#e5b54d', roughness: 0.58, metalness: 0.08 }),
    window: new THREE.MeshStandardMaterial({ color: '#2b3448', emissive: '#f0b45a', emissiveIntensity: 0.55, roughness: 0.3 }),
    pine: new THREE.MeshStandardMaterial({ color: '#355d4e', roughness: 0.95 }),
    pineLight: new THREE.MeshStandardMaterial({ color: '#4c765b', roughness: 0.95 }),
    trunk: new THREE.MeshStandardMaterial({ color: '#665043', roughness: 1 }),
    rope: new THREE.MeshStandardMaterial({ color: '#b69b72', roughness: 1 }),
    metal: new THREE.MeshStandardMaterial({ color: '#315e61', roughness: 0.45, metalness: 0.45 }),
    lamp: new THREE.MeshStandardMaterial({ color: '#f8e2a6', emissive: '#ffb84f', emissiveIntensity: 1.2, roughness: 0.2 }),
    water: new THREE.MeshPhysicalMaterial({ color: '#4f9a9c', roughness: 0.18, metalness: 0.04, clearcoat: 0.65, clearcoatRoughness: 0.18, transparent: true, opacity: 0.9 }),
  };
}

function computeDiagnostics(root: THREE.Object3D): WorldDiagnostics {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  let meshes = 0;
  let instancedMeshes = 0;
  let triangles = 0;
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    meshes += 1;
    if (object instanceof THREE.InstancedMesh) instancedMeshes += 1;
    geometries.add(object.geometry);
    const indexCount = object.geometry.index?.count ?? object.geometry.attributes.position?.count ?? 0;
    const multiplier = object instanceof THREE.InstancedMesh ? object.count : 1;
    triangles += Math.floor(indexCount / 3) * multiplier;
    const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of objectMaterials) {
      materials.add(material);
      for (const value of Object.values(material)) if (value instanceof THREE.Texture) textures.add(value);
    }
  });
  return { meshes, instancedMeshes, geometries: geometries.size, materials: materials.size, textures: textures.size, triangles };
}

export function createLakesideVillage(): LakesideVillageWorld {
  const root = new THREE.Group();
  root.name = 'evergreenLakeVillage';
  const materials = makeMaterials();
  const collisions: THREE.Object3D[] = [];
  const animated: THREE.Object3D[] = [];

  const skyUniforms = {
    uTop: { value: new THREE.Color(PALETTE.morning.skyTop) },
    uHorizon: { value: new THREE.Color(PALETTE.morning.skyHorizon) },
    uSun: { value: new THREE.Color('#fff1c9') },
  };
  const skyMaterial = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: skyUniforms,
    vertexShader: 'varying vec3 vDir; void main(){vDir=normalize(position);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}',
    fragmentShader: 'varying vec3 vDir; uniform vec3 uTop,uHorizon,uSun; void main(){float h=clamp(vDir.y*.5+.5,0.,1.);vec3 c=mix(uHorizon,uTop,pow(h,.7));float glow=pow(max(dot(normalize(vDir),normalize(vec3(.35,.22,.78))),0.),10.);gl_FragColor=vec4(c+uSun*glow*.16,1.);}',
  });
  root.add(createSky(skyMaterial));

  const island = addShadowed(root, new THREE.Mesh(new THREE.CylinderGeometry(28, 31, 2.5, 64), materials.ground));
  island.position.y = -1.22;
  collisions.push(island);
  const lake = new THREE.Mesh(new THREE.CircleGeometry(68, 96), materials.water);
  lake.rotation.x = -Math.PI / 2;
  lake.position.y = -0.15;
  lake.name = 'evergreenLake';
  root.add(lake);
  animated.push(lake);

  const station = createRangerStation(materials);
  station.position.set(-7, 0, 3);
  station.rotation.y = 0.32;
  root.add(station);
  collisions.push(station);

  const cabins: Array<[number, number, number, number]> = [[-14, -2, -0.12, 0.85], [-10, -8, 0.26, 0.8], [11, 4, -0.48, 0.88], [15, -3, -0.7, 0.76]];
  for (const [x, z, rotation, scale] of cabins) {
    const cabin = createCabin(materials, scale);
    cabin.position.set(x, 0, z);
    cabin.rotation.y = rotation;
    root.add(cabin);
    collisions.push(cabin);
  }

  const bridge = createRopeBridge(materials);
  bridge.position.set(0, -0.05, -10.5);
  bridge.rotation.y = 0.08;
  root.add(bridge);
  collisions.push(bridge);

  const dock = new THREE.Group();
  const dockPlank = new THREE.BoxGeometry(1.05, 0.16, 3.1);
  for (let i = 0; i < 8; i += 1) {
    const plank = addShadowed(dock, new THREE.Mesh(dockPlank, materials.bridgeWood));
    plank.position.x = (i - 3.5) * 1.03;
  }
  dock.position.set(5.5, 0.15, 4.4);
  dock.rotation.y = -0.32;
  root.add(dock);

  const pineCount = 82;
  const coneGeometry = new THREE.ConeGeometry(0.9, 3.2, 7);
  const trunkGeometry = new THREE.CylinderGeometry(0.16, 0.24, 2.2, 7);
  const pines = new THREE.InstancedMesh(coneGeometry, materials.pine, pineCount);
  const trunks = new THREE.InstancedMesh(trunkGeometry, materials.trunk, pineCount);
  const matrix = new THREE.Matrix4();
  const color = new THREE.Color();
  for (let i = 0; i < pineCount; i += 1) {
    const angle = (i / pineCount) * Math.PI * 2 + Math.sin(i * 2.17) * 0.14;
    const radius = 20.5 + (i % 9) * 1.65;
    const scale = 0.72 + (i % 7) * 0.07;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    matrix.compose(new THREE.Vector3(x, 2.4 * scale, z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), angle), new THREE.Vector3(scale, scale, scale));
    pines.setMatrixAt(i, matrix);
    color.set(i % 3 === 0 ? '#426d56' : '#315747');
    pines.setColorAt(i, color);
    matrix.compose(new THREE.Vector3(x, 1.1 * scale, z), new THREE.Quaternion(), new THREE.Vector3(scale, scale, scale));
    trunks.setMatrixAt(i, matrix);
  }
  pines.castShadow = pines.receiveShadow = true;
  trunks.castShadow = trunks.receiveShadow = true;
  pines.instanceMatrix.needsUpdate = trunks.instanceMatrix.needsUpdate = true;
  pines.instanceColor!.needsUpdate = true;
  root.add(pines, trunks);

  const lanterns: THREE.Group[] = [];
  for (const [x, z] of [[-4, 5], [0, 5.8], [4.2, 5.1], [-9, -4.3], [9, -3.5]] as Array<[number, number]>) {
    const lantern = createLantern(materials);
    lantern.position.set(x, 0, z);
    root.add(lantern);
    lanterns.push(lantern);
  }

  const city = new THREE.Group();
  city.name = 'distantCity';
  const buildingMaterials = [
    new THREE.MeshStandardMaterial({ color: '#596178', roughness: 0.8 }),
    new THREE.MeshStandardMaterial({ color: '#6c6780', roughness: 0.82 }),
    new THREE.MeshStandardMaterial({ color: '#516879', roughness: 0.78 }),
  ];
  for (let i = 0; i < 28; i += 1) {
    const width = 1.1 + (i % 4) * 0.35;
    const height = 4 + (i % 7) * 1.45;
    const building = new THREE.Mesh(new THREE.BoxGeometry(width, height, 1.8), buildingMaterials[i % buildingMaterials.length]);
    building.position.set((i - 13.5) * 1.8, height * 0.5 - 1.1, -48 - (i % 3) * 2.2);
    city.add(building);
    const crown = new THREE.Mesh(new THREE.BoxGeometry(width * 0.72, 0.12, 1.85), materials.window);
    crown.position.set(building.position.x, building.position.y + height * 0.43, building.position.z + 0.94);
    city.add(crown);
  }
  root.add(city);

  const anchors: Record<string, THREE.Vector3> = {
    mirror: new THREE.Vector3(-3.8, 0, 8.2),
    breakfast: new THREE.Vector3(-8.2, 0, 8.4),
    'dock-race': new THREE.Vector3(5.3, 0.15, 4.3),
    pawprint: new THREE.Vector3(10.2, 0, -5.8),
    bridge: new THREE.Vector3(0, 1.05, -10.4),
    'forest-dog': new THREE.Vector3(11.5, 0, -11.6),
    'city-lights': new THREE.Vector3(0, 0.05, -17.5),
  };

  const mirrorSet = new THREE.Group();
  mirrorSet.name = 'storyProp:mirror';
  const mirrorSurface = new THREE.Mesh(
    new THREE.PlaneGeometry(2.25, 2.9),
    new THREE.MeshPhysicalMaterial({ color: '#9fc9cd', roughness: 0.12, metalness: 0.42, clearcoat: 0.8 }),
  );
  mirrorSurface.position.set(0, 1.78, -1.35);
  mirrorSet.add(mirrorSurface);
  for (const [x, y, w, h] of [[-1.22, 1.78, 0.18, 3.2], [1.22, 1.78, 0.18, 3.2], [0, 0.22, 2.62, 0.18], [0, 3.34, 2.62, 0.18]] as Array<[number, number, number, number]>) {
    const frame = addShadowed(mirrorSet, new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.18), materials.trim));
    frame.position.set(x, y, -1.27);
  }
  mirrorSet.position.copy(anchors.mirror);
  root.add(mirrorSet);

  const breakfastSet = new THREE.Group();
  breakfastSet.name = 'storyProp:breakfastTable';
  const tabletop = addShadowed(breakfastSet, new THREE.Mesh(new THREE.CylinderGeometry(2.1, 2.1, 0.18, 16), materials.wood));
  tabletop.position.y = 1.05;
  for (const [x, z] of [[-1.4, -1.1], [1.4, -1.1], [-1.4, 1.1], [1.4, 1.1]] as Array<[number, number]>) {
    const leg = addShadowed(breakfastSet, new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.14, 1.05, 8), materials.darkWood));
    leg.position.set(x, 0.52, z);
  }
  breakfastSet.position.copy(anchors.breakfast);
  root.add(breakfastSet);

  const pawprintSet = new THREE.Group();
  pawprintSet.name = 'storyProp:giantPawprint';
  const mud = new THREE.MeshStandardMaterial({ color: '#3d4b3d', roughness: 1 });
  const heel = new THREE.Mesh(new THREE.SphereGeometry(0.95, 18, 10), mud);
  heel.scale.set(1, 0.08, 0.82);
  heel.position.set(0, 0.04, 0.35);
  pawprintSet.add(heel);
  for (const [x, z, scale] of [[-0.72, -0.62, 0.62], [-0.24, -0.92, 0.72], [0.3, -0.9, 0.7], [0.75, -0.55, 0.58]] as Array<[number, number, number]>) {
    const toe = new THREE.Mesh(new THREE.SphereGeometry(0.43, 14, 8), mud);
    toe.scale.set(scale, 0.12, scale * 1.15);
    toe.position.set(x, 0.05, z);
    pawprintSet.add(toe);
  }
  pawprintSet.position.copy(anchors.pawprint);
  root.add(pawprintSet);

  let currentTime: TimeOfDay = 'morning';
  const setTimeOfDay = (time: TimeOfDay): void => {
    currentTime = time;
    const palette = PALETTE[time];
    skyUniforms.uTop.value.set(palette.skyTop);
    skyUniforms.uHorizon.value.set(palette.skyHorizon);
    materials.window.emissiveIntensity = palette.practical;
    materials.lamp.emissiveIntensity = palette.practical * 1.5;
    materials.water.color.set(time === 'blue-hour' ? '#365d83' : time === 'forest' ? '#4a827a' : '#4f9a9c');
  };

  return {
    root,
    anchors,
    collisions,
    diagnostics: computeDiagnostics(root),
    update: (_delta, elapsed) => {
      lake.position.y = -0.15 + Math.sin(elapsed * 0.55) * 0.016;
      lake.rotation.z = Math.sin(elapsed * 0.08) * 0.003;
      lanterns.forEach((lantern, index) => {
        const bulb = lantern.getObjectByName('practicalLight');
        if (bulb) bulb.scale.setScalar(1 + Math.sin(elapsed * 2.1 + index) * 0.035);
      });
      if (currentTime === 'blue-hour') city.position.y = Math.sin(elapsed * 0.15) * 0.015;
    },
    setTimeOfDay,
    dispose: () => {
      const disposedGeometries = new Set<THREE.BufferGeometry>();
      const disposedMaterials = new Set<THREE.Material>();
      root.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        if (!disposedGeometries.has(object.geometry)) {
          object.geometry.dispose();
          disposedGeometries.add(object.geometry);
        }
        const objectMaterials = Array.isArray(object.material) ? object.material : [object.material];
        objectMaterials.forEach((material) => {
          if (!disposedMaterials.has(material)) {
            material.dispose();
            disposedMaterials.add(material);
          }
        });
      });
      textureCache.forEach((texture) => texture.dispose());
      root.clear();
    },
  };
}

export function createVillageLightingRig(): VillageLightingRig {
  const root = new THREE.Group();
  const sun = new THREE.DirectionalLight(PALETTE.morning.sun, PALETTE.morning.sunIntensity);
  sun.position.set(-14, 22, 12);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -28;
  sun.shadow.camera.right = 28;
  sun.shadow.camera.top = 26;
  sun.shadow.camera.bottom = -24;
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 85;
  sun.shadow.bias = -0.0004;
  const hemisphere = new THREE.HemisphereLight('#d9edf0', '#435342', PALETTE.morning.hemi);
  const rim = new THREE.DirectionalLight('#a6c9d8', 0.55);
  rim.position.set(12, 9, -14);
  root.add(sun, hemisphere, rim);
  return {
    root,
    sun,
    hemisphere,
    rim,
    setTimeOfDay: (time) => {
      const palette = PALETTE[time];
      sun.color.set(palette.sun);
      sun.intensity = palette.sunIntensity;
      hemisphere.intensity = palette.hemi;
      hemisphere.color.set(time === 'blue-hour' ? '#8ea3c5' : '#d9edf0');
      hemisphere.groundColor.set(time === 'forest' ? '#2f4b3c' : '#5c5a49');
      rim.intensity = time === 'blue-hour' ? 0.95 : 0.5;
    },
  };
}
